import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import { WSQueryCache } from "@ws-react-query";
import { useCallback, useMemo, useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { QUERY_PAYOUT_SETTINGS } from "../../../../query/payments/keys";
import { useQueryPayoutSettings } from "../../../../query/payments/queries";
import {
  useQueryAccounts,
  useQueryMemberProfile
} from "../../../../query/users/queries";
import { paymentsService } from "../../../../services/payments";
import { FormAddDebitCard } from "../../../../shared/components/FormAddDebitCard";
import { FormManuallyAddAccount } from "../../../../shared/components/FormManuallyAddAccount";
import { FormType } from "../../../../shared/components/FormManuallyAddAccount/FormType";
import {
  ManualAccountSubType,
  ManualAccountType
} from "../../../../shared/components/FormManuallyAddAccount/types";
import { FormManuallyAddInternationalAccount } from "../../../../shared/components/FormManuallyAddInternationalAccount";
import { StepLayoutWithOptions } from "../../../../shared/components/Layout/StepLayoutWithOptions";
import {
  avatarBankAccount,
  avatarInstantPayout,
  avatarInternationalBankAccount,
  avatarPlaid,
  avatarPlatformBalance,
  avatarUSBankAccount,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { usePaymentCardForm } from "../../../../shared/hooks/usePaymentCardForm";
import { selectorAccountNumber } from "../../../../shared/selectors/selectorAccountNumber";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorInstantPayoutDefaultFee } from "../../../../shared/selectors/selectorInstantPayoutDefaultFee";
import { selectorPendingBankingAccount } from "../../../../shared/selectors/selectorPendingBankingAccount";
import { selectorUseInternationalPayouts } from "../../../../shared/selectors/selectorUseInternationalPayouts";
import { isEligibleRoutingNumber } from "../../../../shared/utils/isEligibleRoutingNumber";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { useWSPlaidLinkExtended } from "../../../../utils/useWSPlaidLinkExtended";
import { FormCreateInternalAccount } from "../../../Onboarding/components/FormCreateInternalAccount";
import { FormAddPlatformBalance } from "../../../PlatformBalance/components/FormAddPlatformBalance";
import { selectorAccountRtpReady } from "../../../Transfer/components/FormTransfer/selectorAccountRtpReady";
import { selectorAccountWithdrawalReady } from "../../../Transfer/components/FormTransfer/selectorAccountWithdrawalReady";
import { selectorInstantPlaidRoute } from "../../selectors/selectorInstantPlaidRoute";
import { FormSelectAccount } from "../FormSelectAccount";
import { ReconnectPlaidAccounts } from "../ReconnectPlaidAccounts";
import { RouteInstantAccountSelect } from "./RouteInstantAccountSelect";
import { InstantUnavailable } from "./RouteInstantUnavailable";

export type PayoutMethodType =
  | "instant"
  | "standard"
  | "wallet"
  | "international"
  | "platform-balance";

export type PayoutMethodInstantType = "instant-plaid" | "instant-manual-type";

export type PayoutMethodStandardType =
  | "standard-plaid"
  | "standard-manual-type";

type Props = {
  basePath: string;
  onBack?: () => void;
  onNext?: () => void;
  onError?: (error: WSServiceError) => void;
  availableUsMethods?: PayoutMethodType[];
  availableInternationalMethods?: PayoutMethodType[];
  localCurrencyOnly?: boolean;
  lockCountry?: boolean;
};

export const FlowAddPayoutMethod: React.FC<Props> = ({
  basePath,
  onBack,
  onNext,
  onError,
  availableUsMethods,
  availableInternationalMethods,
  localCurrencyOnly,
  lockCountry
}) => {
  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);
  const queryCustomerEntity = useQueryCustomerEntity();
  const isInternational = queryMember.data
    ? selectorUseInternationalPayouts(
        queryMember.data,
        queryCustomerEntity.data
      )
    : false;

  const queryPayoutSettings = useQueryPayoutSettings(userId);
  const queryFeatureFlags = useFeatureFlags();
  const queryInternalAccounts = useQueryInternalAccounts();
  const hasActiveWallet = (queryInternalAccounts.data || []).some(
    selectorActiveBankingAccount
  );
  const hasPendingWallet = (queryInternalAccounts.data || []).some(
    selectorPendingBankingAccount
  );

  const paymentCardForm = usePaymentCardForm();

  const history = useHistory();
  const [createdAccounts, setCreatedAccounts] = useState<IAccount[]>([]);
  const [failedAccount, setFailedAccount] = useState<IAccount | undefined>(
    undefined
  );
  const queryAccounts = useQueryAccounts();
  const allStandardAccounts = useMemo(
    () => (queryAccounts.data || []).filter(selectorAccountWithdrawalReady),
    [queryAccounts.data]
  );
  const allRtpAccounts = useMemo(
    () => (queryAccounts.data || []).filter(selectorAccountRtpReady),
    [queryAccounts.data]
  );

  const [setInstantPayout] = useWSMutation<
    void,
    WSServiceError,
    { accountId: string }
  >(
    async ({ accountId }) => {
      const payoutSettings = await paymentsService.payoutSettings.update(
        userId,
        {
          payoutPreferences: PayoutPreferences.Instant,
          instant: {
            destinationId: accountId,
            destinationType: PayoutDestinationType.Account
          }
        }
      );

      WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
      onNext?.();
    },
    {
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const { openSnackbar } = useWSSnackbar();

  const instantPlaidLink = useWSPlaidLinkExtended({
    onSuccess: accounts => {
      const { path, accounts: routedAccounts } =
        selectorInstantPlaidRoute(accounts);

      setCreatedAccounts(routedAccounts);
      history.push(basePath + "/" + path);
    }
  });

  const standardPlaidLink = useWSPlaidLinkExtended({
    onSuccess: accounts => {
      const filteredAccounts = accounts.filter(selectorAccountWithdrawalReady);
      const filteredAccountsWithNumbers = filteredAccounts.filter(
        selectorAccountNumber
      );

      if (filteredAccounts.length > 0) {
        if (filteredAccountsWithNumbers.length > 0) {
          setCreatedAccounts(filteredAccountsWithNumbers);
          history.push(basePath + "/standard-plaid");
        } else {
          setCreatedAccounts(accounts);
          history.push(basePath + "/standard-plaid-reconnect");
        }
      } else {
        openSnackbar({
          type: "error",
          message: "Account can not be used as a payout method"
        });
      }
    }
  });

  const [standardManualAccountType, setStandardManualAccountType] = useState<{
    type: ManualAccountType;
    subType: ManualAccountSubType;
  }>({
    type: "personal",
    subType: "checking"
  });

  const [instantManualAccountType, setInstantManualAccountType] = useState<{
    type: ManualAccountType;
    subType: ManualAccountSubType;
  }>({
    type: "personal",
    subType: "checking"
  });

  const filterOptionsByAvailableMethods = <O extends { value: string }>(
    options: O[],
    availableMethods: PayoutMethodType[] | undefined
  ): O[] => {
    if (!availableMethods) {
      return options;
    }
    const allowedValues = availableMethods as string[];
    return options.filter(option => allowedValues.includes(option.value));
  };

  return (
    <Switch>
      <Route path={basePath + "/type"}>
        <StepLayoutWithOptions<PayoutMethodType>
          key="type"
          title="Add payout method"
          description="Add a payout method to deposit your income"
          disabledPosition="bottom"
          infoBox={value => {
            if (value === "platform-balance") {
              return {
                title: "Platform Balance",
                children: (
                  <>
                    <p>
                      You have a Platform Balance available to you to hold your
                      earnings securely in a preferred currency of your choice
                      until you are ready to transfer it out to the bank account
                      you’ve added. This is a benefit available to you for free
                      with the Wingspan platform.
                    </p>

                    <p>
                      <b>Why is this offered?</b> Some non-U.S. contractors
                      prefer to keep their earnings on the platform in a
                      preferred currency until they are ready to transfer the
                      balance to their external bank account for various reasons
                      (such as exchange rates and inflation). Additionally, in
                      the event that a payout to the external bank account fails
                      repeatedly, the earnings will be held in the Platform
                      Balance - allowing contractors to transfer it out once any
                      issues are resolved.
                    </p>
                  </>
                )
              };
            }
          }}
          options={
            isInternational
              ? filterOptionsByAvailableMethods(
                  [
                    {
                      header: {
                        label: {
                          text: "Non-U.S. account",
                          helperText:
                            "Wingspan will deposit into your bank account in the currency of that account in a non-U.S. country or in U.S.-dollars for USD accounts.",
                          avatar: avatarInternationalBankAccount
                        }
                      },
                      value: "international"
                    },
                    {
                      header: {
                        label: {
                          text: "U.S. based account",
                          helperText:
                            "Wingspan will keep your deposits in USD and deposit it in your U.S.-based account.",

                          avatar: avatarUSBankAccount
                        }
                      },
                      value: "standard"
                    },
                    ...(queryFeatureFlags.data?.platformBalance
                      ? [
                          {
                            header: {
                              label: {
                                text: "Platform Balance",
                                helperText:
                                  "Hold your earnings securely on the platform until you're ready to receive them.",
                                avatar: avatarPlatformBalance
                              }
                            },
                            value: "platform-balance"
                          }
                        ]
                      : [])
                  ],
                  availableInternationalMethods
                )
              : filterOptionsByAvailableMethods(
                  [
                    {
                      header: {
                        label: {
                          text: `Instant payout (${selectorInstantPayoutDefaultFee(
                            queryPayoutSettings.data
                          )}% fee)`,
                          helperText:
                            "Fastest. Receive your payments immediately when sent to eligible bank & debit cards.",
                          avatar: avatarInstantPayout
                        }
                      },
                      value: "instant"
                    },
                    {
                      header: {
                        label: {
                          text: "Wingspan Wallet (free)",
                          helperText:
                            "Faster. Free business bank & debit card.",
                          avatar: avatarWallet
                        },
                        ...(hasActiveWallet
                          ? {
                              value: {
                                pill: {
                                  theme: "success" as const,
                                  text: "Already activated",
                                  badge: true,
                                  type: "noBackground" as const
                                },
                                stacked: true
                              }
                            }
                          : hasPendingWallet
                            ? {
                                value: {
                                  pill: {
                                    theme: "blue" as const,
                                    text: "Pending activation",
                                    badge: true,
                                    type: "noBackground" as const
                                  },
                                  stacked: true
                                }
                              }
                            : {})
                      },
                      disabled: hasActiveWallet || hasPendingWallet,
                      value: "wallet"
                    },
                    {
                      header: {
                        label: {
                          text: "Bank account (free)",
                          helperText:
                            "Standard. Connect your existing bank account.",
                          avatar: avatarBankAccount
                        }
                      },
                      value: "standard"
                    }
                  ],
                  availableUsMethods
                )
          }
          onBack={onBack}
          onNext={value => {
            history.push(basePath + "/" + value);
          }}
        />
      </Route>

      <Route path={basePath + "/instant"}>
        <StepLayoutWithOptions<PayoutMethodInstantType>
          key="instant"
          title={`Add instant payout method (${selectorInstantPayoutDefaultFee(
            queryPayoutSettings.data
          )}% fee)`}
          description={`Receive your funds in under an hour. A ${selectorInstantPayoutDefaultFee(
            queryPayoutSettings.data
          )}% fee will be applied when the transaction is made.`}
          options={[
            {
              header: {
                label: {
                  text: "Link account with Plaid",
                  helperText:
                    "Instantly connect your bank accounts securely (Instant verification)",
                  avatar: avatarPlaid
                }
              },
              value: "instant-plaid"
            },
            {
              header: {
                label: {
                  text: "Set up account manually",
                  helperText: "Manually enter your routing and account numbers",
                  avatar: avatarBankAccount
                }
              },
              value: "instant-manual-type"
            }
          ]}
          infoBox={{
            theme: "info",
            children:
              "Note: Not all banks support Real-Time Payments. We'll check if your bank supports instant payouts when you connect an account."
          }}
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onNext={value => {
            if (value === "instant-plaid") {
              instantPlaidLink.open();
            } else {
              history.push(basePath + "/" + value);
            }
          }}
        />
      </Route>

      <Route path={basePath + "/standard"}>
        <StepLayoutWithOptions<PayoutMethodStandardType>
          key="stanrard"
          title="Add bank account (free)"
          description="Add a bank account and receive your funds in the standard 2-5 business days."
          options={[
            {
              header: {
                label: {
                  text: "Link account with Plaid",
                  helperText:
                    "Instantly verify and connect your bank accounts securely",
                  avatar: avatarPlaid
                }
              },
              value: "standard-plaid"
            },
            {
              header: {
                label: {
                  text: "Set up account manually",
                  helperText: "Manually enter your routing and account numbers",
                  avatar: avatarBankAccount
                }
              },
              value: "standard-manual-type"
            }
          ]}
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onNext={value => {
            if (value === "standard-plaid") {
              standardPlaidLink.open();
            } else {
              history.push(basePath + "/" + value);
            }
          }}
        />
      </Route>

      <Route path={basePath + "/wallet"}>
        <FormCreateInternalAccount
          type="Banking"
          currency="USD"
          setStandardPayoutDestination
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onContinue={onNext}
        />
      </Route>

      <Route path={basePath + "/instant-card"}>
        <FormAddDebitCard
          setInstantPayoutPreference
          updateInstantDestinaiton
          onBack={() => {
            history.push(basePath + "/instant");
          }}
          onSuccess={onNext}
          withPanel
        />
      </Route>

      <Route path={basePath + "/instant-plaid"}>
        <RouteInstantAccountSelect
          accounts={
            createdAccounts.length > 0 ? createdAccounts : allRtpAccounts
          }
          onBack={() => {
            history.push(basePath + "/instant");
          }}
          onNext={onNext}
          onDebitCard={() => {
            if (queryFeatureFlags.data?.paymentCardAsPayoutMethod) {
              paymentCardForm.open({
                onSuccess: onNext,
                onError: onError,
                setAsInstantPayoutMethod: true
              });
            } else {
              history.push(basePath + "/instant-card");
            }
          }}
          onTryAnotherBank={() => {
            history.push(basePath + "/instant");
          }}
          onActivateWallet={() => {
            history.push(basePath + "/wallet");
          }}
        />
      </Route>

      <Route path={basePath + "/instant-plaid-reconnect"}>
        <ReconnectPlaidAccounts
          accounts={createdAccounts}
          onBack={() => {
            history.push(basePath + "/instant");
          }}
          onReconnect={() => {
            instantPlaidLink.open();
          }}
        />
      </Route>

      <Route path={basePath + "/instant-manual-type"}>
        <FormType
          value={instantManualAccountType}
          onBack={() => {
            history.push(basePath + "/instant");
          }}
          onNext={newValue => {
            setInstantManualAccountType(newValue);
            history.push(basePath + "/instant-manual");
          }}
        />
      </Route>

      <Route path={basePath + "/instant-manual"}>
        <FormManuallyAddAccount
          onBack={() => {
            history.push(basePath + "/instant-manual-type");
          }}
          onSuccess={useCallback(
            async (account: IAccount) => {
              const routing = account.numbers?.routing ?? "";

              if (isEligibleRoutingNumber(routing)) {
                await setInstantPayout({ accountId: account.accountId });
              } else {
                setFailedAccount(account);
                history.push(basePath + "/instant-unavailable");
              }
            },
            [basePath, history, setInstantPayout]
          )}
          withPanel
          type={instantManualAccountType.type}
          subType={instantManualAccountType.subType}
        />
      </Route>

      <Route path={basePath + "/instant-unavailable"}>
        <InstantUnavailable
          failedAccount={failedAccount}
          onBack={() => {
            history.push(basePath + "/instant");
          }}
          onNext={onNext}
          onDebitCard={() => {
            if (queryFeatureFlags.data?.paymentCardAsPayoutMethod) {
              paymentCardForm.open({
                onSuccess: onNext,
                onError: onError,
                setAsInstantPayoutMethod: true
              });
            } else {
              history.push(basePath + "/instant-card");
            }
          }}
          onTryAnotherBank={() => {
            history.push(basePath + "/instant");
          }}
          onActivateWallet={() => {
            history.push(basePath + "/wallet");
          }}
        />
      </Route>

      <Route path={basePath + "/standard-plaid"}>
        <FormSelectAccount
          title="Set default payout method"
          description="Select an account you added through Plaid as your payout method."
          setStandardPayoutDestination
          accounts={
            createdAccounts.length > 0 ? createdAccounts : allStandardAccounts
          }
          onBack={() => {
            history.push(basePath + "/standard");
          }}
          onNext={onNext}
        />
      </Route>

      <Route path={basePath + "/standard-plaid-reconnect"}>
        <ReconnectPlaidAccounts
          accounts={createdAccounts}
          onBack={() => {
            history.push(basePath + "/standard");
          }}
          onReconnect={() => {
            standardPlaidLink.open();
          }}
        />
      </Route>

      <Route path={basePath + "/standard-manual-type"}>
        <FormType
          value={standardManualAccountType}
          onBack={() => {
            history.push(basePath + "/ach");
          }}
          onNext={newValue => {
            setStandardManualAccountType(newValue);
            history.push(basePath + "/standard-manual");
          }}
        />
      </Route>

      <Route path={basePath + "/standard-manual"}>
        <FormManuallyAddAccount
          setStandardPayoutDestination
          onBack={() => {
            history.push(basePath + "/standard-manual-type");
          }}
          onSuccess={onNext}
          withPanel
          type={standardManualAccountType.type}
          subType={standardManualAccountType.subType}
        />
      </Route>

      <Route path={basePath + "/international"}>
        <FormManuallyAddInternationalAccount
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onSuccess={onNext}
          withPanel
          localCurrencyOnly={localCurrencyOnly}
          lockCountry={lockCountry}
        />
      </Route>

      <Route path={basePath + "/platform-balance"}>
        <FormAddPlatformBalance
          onBack={() => {
            history.push(basePath + "/type");
          }}
          onContinue={onNext}
        />
      </Route>

      <Redirect path={basePath} to={basePath + "/type"} />
    </Switch>
  );
};
